import { differenceInDays, parse } from "date-fns";
import { useEffect } from "react";
import { toast } from "react-toastify";

import { DATE_FORMATS_FNS } from "components";
import { typedQueryClient, useTypedQuery } from "hooks/useTypedQuery";
import { useDealerLicensesDashboardFilters } from "modules/DealerLicenseOverview/hooks/useDealerLicensesFilters";
import { getPaginatedInvoices, setPaginatedInvoices, usePaginatedInvoices } from "modules/DealerLicenseOverview/hooks/usePaginatedInvoices";
import { DealerLicenseQueryKeys } from "modules/DealerLicenseOverview/queryKeys";
import { ApiInstance } from "utils/Api";

import type { HistoricalInvoice, PaginatedHistoricalInvoices } from "modules/LocationLicenseOverview/queryKeys";

export const useDealerInvoicesQuery = (paginationUrl = "", currentInvoicePage = 0) => {
  const paginatedInvoices = usePaginatedInvoices();
  const { dealer } = useDealerLicensesDashboardFilters();

  const getSortedResults = (data: HistoricalInvoice[]) =>
    [...data].sort((a, b) =>
      differenceInDays(parse(b.Modified, DATE_FORMATS_FNS.dateMonthYearSlash, new Date()), parse(a.Modified, DATE_FORMATS_FNS.dateMonthYearSlash, new Date()))
    );

  const handleUpdatePaginatedInvoices = (data: PaginatedHistoricalInvoices) => {
    const existingDealer = paginatedInvoices.data.find(element => element.dealer === dealer?.id);

    if (!existingDealer) {
      if (!dealer?.id) return { data: [] };
      const newDealer = { dealer: dealer.id, data: { [currentInvoicePage]: data } };
      return { data: [...paginatedInvoices.data, newDealer] };
    }

    const updatedData = { ...paginatedInvoices };
    const existingDealerIndex = updatedData.data.findIndex(element => element.dealer === dealer?.id);

    if (existingDealerIndex !== -1) updatedData.data[existingDealerIndex] = { ...existingDealer, data: { ...existingDealer.data, [currentInvoicePage]: data } };

    return { data: updatedData.data };
  };

  const query = useTypedQuery({
    queryKey: DealerLicenseQueryKeys.invoices,
    queryFn: async ({ endpoint, baseUrl }) => {
      const invoices = getPaginatedInvoices();

      if (invoices.data.length > 0) {
        const existingDealerData = paginatedInvoices.data.find(element => element.dealer === dealer?.id);
        if (existingDealerData?.data[currentInvoicePage]) return existingDealerData.data[currentInvoicePage];
      }

      const response = await ApiInstance.post(
        endpoint,
        { dealer_id: dealer?.id, ...(paginationUrl && { pagination_url: paginationUrl }), page_index: currentInvoicePage },
        baseUrl
      );

      setPaginatedInvoices(handleUpdatePaginatedInvoices(response.data)); // TODO: why is backend data saved in a local key ...

      return {
        __next: response.data.__next,
        results: getSortedResults(response.data.results)
      };
    },
    enabled: Boolean(dealer && dealer.is_exact_invoicing_enabled)
  });

  useEffect(() => {
    if (dealer && dealer.is_exact_invoicing_enabled) query.refetch();
    else if (!dealer?.is_exact_invoicing_enabled) typedQueryClient.resetQueries(DealerLicenseQueryKeys.invoices);
  }, [dealer]);

  useEffect(() => {
    if (query.error) toast.error(query.error.message);
  }, [query.error]);

  return {
    dealerInvoices: query.data,
    errorDealerInvoices: query.error,
    isLoadingDealerInvoices: query.isLoading,
    isDealerInvoicesRefetching: query.isRefetching,
    paginatedDealerInvoices: paginatedInvoices,
    refetchInvoices: query.refetch
  };
};
